<script lang="ts" setup>
  import type { OrderItemComponent } from '@storefront/resources';
  import { useShared } from '@othercommerce/foundation';
  import { computed } from 'vue';
  import { useUnitFormatter } from '@othercommerce/domain';

  interface Props {
    component: OrderItemComponent,
  }

  const props = defineProps<Props>();
  const shared = useShared();
  const unitFormatter = useUnitFormatter();

  const price = computed(() => {
    if (shared.value.includeVat) {
      return props.component.priceGross;
    } else {
      return props.component.priceNet;
    }
  });

  const quantity = computed(() => {
    return unitFormatter.short(props.component.stockUnit, props.component.amount);
  });
</script>
<template>
  <div class="cart-item-component">
    <div class="component-left">
      <div class="component-photo">
        <MediaPicture :photo="component.photo" sizes="80x80 2x, 40x40" />
      </div>
      <div class="component-name">
        <div class="component-title">{{ component.variantTitle }}</div>
        <div class="component-sku">{{ component.variantSku }}</div>
      </div>
    </div>
    <div class="component-right">
      <div class="component-price">
        <div class="component-quantity" v-html="quantity" />
        <div class="component-multiplier">&times;</div>
        <div class="component-price-value">
          <template v-if="price > 0">
            <SfMoney :value="price" />
          </template>
          <template v-else>
            <data :value="price" class="component-price-free" v-html="$t('SHOP.CART.LABEL.FREE_ITEM')" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-cart-item-font-size: 0.75rem;
    --sf-cart-item-font-size-sku: 0.625rem;
    --sf-cart-item-font-size-unit: 0.625rem;

    --sf-cart-item-gap: 0.5rem;
    --sf-cart-item-gap-sm: 0.25rem;
    --sf-cart-item-title-font-weight: var(--font-weight-semi);
    --sf-cart-item-price-font-weight: var(--font-weight-bold);

    --sf-cart-item-color: var(--text-color);
    --sf-cart-item-color-sku: var(--secondary-color);
    --sf-cart-item-color-unit: var(--secondary-color);
    --sf-cart-item-color-free: var(--primary-color);
    --sf-cart-item-color-quantity: var(--secondary-color);

    --sf-cart-item-photo-width: 40px;
  }

  .cart-item-component {
    font-size: var(--sf-cart-item-font-size);
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--sf-cart-item-gap);

    @include media.query(sm) {
      grid-template-columns: 1fr;
      gap: var(--sf-cart-item-gap-sm);
    }

    .component-left,
    .component-right {
      display: flex;
    }

    .component-right {
      flex-direction: column;
      align-items: flex-end;
    }

    .component-left {
      gap: var(--sf-cart-item-gap-sm);
    }

    .component-photo {
      min-width: var(--sf-cart-item-photo-width);
    }

    .component-name {
      .component-title {
        font-weight: var(--sf-cart-item-title-font-weight);
      }

      .component-sku {
        color: var(--sf-cart-item-color-sku);
        font-size: var(--sf-cart-item-font-size-sku);
      }
    }

    .component-price {
      display: flex;
      gap: var(--sf-cart-item-gap-sm);
      align-items: center;

      .component-price-value {
        font-weight: var(--sf-cart-item-price-font-weight);
      }

      .component-price-unit {
        color: var(--sf-cart-item-color-unit);
        font-size: var(--sf-cart-item-font-size-unit);
      }

      .component-price-free {
        color: var(--sf-cart-item-color-free);
      }
    }

    .component-quantity {
      color: var(--sf-cart-item-color-quantity);
      text-align: right;
    }
  }
</style>
